import { BaseRoute } from '@returnmates/client-core/src/graphql/generated/api'
import moment, { MomentInput } from 'moment-timezone'

export interface FormattedRouteData extends Omit<BaseRoute, 'offerTotalPrice' | 'duration'> {
  duration: string[] | null
  offerTotalPrice: string[]
  [key: string]: unknown
}

// *LA_A_2025-01-10 -> LA A
export const extractName = (input: string): string => {
  return input
    .replace(/\*/g, '') // Remove all asterisks (*)
    .replace(/_\d{4}-\d{2}-\d{2}.*/, '') // Remove underscore + date format (_YYYY-MM-DD and everything after)
    .replace(/_/g, ' ') // Replace remaining underscores with spaces
    .trim()
}

const getDuration = ({
  actualStartTime,
  actualEndTime,
  plannedDuration,
}: Pick<BaseRoute, 'actualEndTime' | 'actualStartTime' | 'plannedDuration'>) => {
  const now = moment()

  const calculateDuration = (start: MomentInput, end: MomentInput) => {
    const duration = moment.duration(moment(end).diff(moment(start)))
    const totalHours = Math.floor(duration.asHours())
    const minutes = duration.minutes()

    if (isNaN(totalHours) || isNaN(minutes)) {
      return
    }

    return `${totalHours}h ${minutes}m`
  }

  const calculatePlannedDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (isNaN(hours) || isNaN(minutes)) {
      return null
    }

    return `${hours}h ${minutes}m`
  }

  // PENDING - show estimated
  if (!actualStartTime && !actualEndTime && plannedDuration) {
    const duration = calculatePlannedDuration(plannedDuration)
    return duration ? [duration, 'Estimated'] : null
  }

  // IN_PROGRESS - current time - launched
  if (actualStartTime && !actualEndTime) {
    const duration = calculateDuration(actualStartTime, now)
    return duration ? [duration] : null
  }

  // COMPLETED - end - start
  if (actualStartTime && actualEndTime) {
    const duration = calculateDuration(actualStartTime, actualEndTime)
    return duration ? [duration] : null
  }

  // Fallback: show estimated if exists
  if (plannedDuration) {
    const duration = calculatePlannedDuration(plannedDuration)
    return duration ? [duration] : null
  }

  return null
}

export const getFormattedData = (data: BaseRoute[]): FormattedRouteData[] =>
  data.map(item => ({
    ...item,
    routeId: item.id,
    name: extractName(item.name),
    origin: item.hubName,
    scheduledDate: moment(item.scheduledStartTime).format('MM/DD/YYYY'),
    launchedAt: item?.actualStartTime
      ? moment(item.actualStartTime).format('MM/DD/YY h:mm A')
      : '-',
    completedAt: item?.actualEndTime ? moment(item.actualEndTime).format('MM/DD/YY h:mm A') : '-',
    duration: getDuration({
      actualEndTime: item?.actualEndTime,
      actualStartTime: item?.actualStartTime,
      plannedDuration: item?.plannedDuration,
    }),
    driverName: item.driverName ? extractName(item.driverName) : 'Unassigned',
    offerTotalPrice: item.offerAdjustment
      ? [`$${item.offerTotalPrice}`, `\n $${item.offerAdjustment} adjustment`]
      : [`$${item.offerTotalPrice}`],
  }))

export const isDummyDriver = (driverName: string): boolean => driverName.startsWith('*')
